/**
 * csEngine - pure-TS port of the 2D cove / control-surface geometry engine.
 *
 * Faithful port of interpolate_section, find_split_point and compute_cs_shape
 * from thread-api/app/services/geometry/cs_shape.py (parity proven to ~1e-13;
 * guarded here by the golden fixture test against the real Python outputs).
 * No external dependencies - the numpy ops are reimplemented in plain TS below
 * with IDENTICAL semantics (endpoint-clamped np.interp, exact-endpoint
 * linspace, same operation order).
 *
 * UNITS NOTE (differs from the Python signature on purpose):
 *   The Python takes gap_mm / fillet_mm and internally divides by 304.8 to get
 *   an offset (gap_ft, fillet_ft) which it adds to radii that live in the
 *   section's own coordinate units. In THIS API, `gap` and `fillet` are
 *   ALREADY in section-coordinate units, so they are used DIRECTLY as the
 *   offset:
 *       r_cove  = sl.radius + gap        (NOT gap/304.8)
 *       fillet radius = fillet           (NOT fillet/304.8)
 *   Consequently: computeCsShape(..., gap, ..., fillet) equals
 *   Python compute_cs_shape(..., gap*304.8, ..., fillet*304.8) numerically.
 *
 * Also: the Python takes (xc1, xc2, yh1, yh2); here a single xc and single yh
 * are passed and treated as xc1==xc2==xc and yh1==yh2==yh.
 */

/** A single 2D section point [x, z] in the section's own coordinate units. */
export type CsPoint = [number, number];

/** One cached airfoil section as served by /geometry/sections (contours LE->TE). */
export interface CsSectionRec {
  eta: number;
  y: number;
  x_le: number;
  x_te: number;
  chord: number;
  upper: CsPoint[];
  lower: CsPoint[];
}

/** Section map keyed by eta formatted with toFixed(4), e.g. '0.2500'. */
export type CsSections = Record<string, CsSectionRec>;

/** All 2D contours for a control-surface section (Python compute_cs_shape subset). */
export interface CsShapeResult {
  eta: number;
  chord: number;
  hinge_x: number;
  hinge_z: number;
  su: { x: number; z: number; radius: number };
  sl: { x: number; z: number; radius: number };
  cs_contour_0: { x: number[]; z: number[] };
  cs_contour_defl: { x: number[]; z: number[] };
  wing_contour: { x: number[]; z: number[] };
  cove_x: number[];
  cove_z: number[];
  spiral_x: number[];
  spiral_z: number[];
  spiral_defl_x: number[];
  spiral_defl_z: number[];
}

// ---------------------------------------------------------------------------
// numpy-equivalent helpers (1-D)
// ---------------------------------------------------------------------------

/** np.linspace(start, stop, num) inclusive of both ends (exact endpoint). */
function linspace(start: number, stop: number, num: number): number[] {
  const out = new Array<number>(num);
  if (num === 1) {
    out[0] = start;
    return out;
  }
  const step = (stop - start) / (num - 1);
  for (let i = 0; i < num; i++) out[i] = start + step * i;
  // ensure exact endpoint like numpy
  out[num - 1] = stop;
  return out;
}

/**
 * np.interp(x, xp, fp) for a scalar x: piecewise-linear interpolation.
 * Matches numpy: assumes xp increasing; clamps to endpoints outside range.
 */
function interpScalar(xi: number, xp: number[], fp: number[]): number {
  const n = xp.length;
  if (n === 0) return NaN;
  // clamp at ends (numpy default: returns fp[0] / fp[n-1])
  if (xi <= xp[0]!) return fp[0]!;
  if (xi >= xp[n - 1]!) return fp[n - 1]!;
  // binary search for the interval [xp[lo], xp[lo+1]] containing xi
  let lo = 0;
  let hi = n - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (xp[mid]! <= xi) lo = mid;
    else hi = mid;
  }
  const x0 = xp[lo]!;
  const x1 = xp[lo + 1]!;
  const y0 = fp[lo]!;
  const y1 = fp[lo + 1]!;
  if (x1 === x0) return y0;
  const t = (xi - x0) / (x1 - x0);
  return y0 + t * (y1 - y0);
}

/** Index of the minimum value. */
function argmin(arr: number[]): number {
  let idx = 0;
  let best = arr[0]!;
  for (let i = 1; i < arr.length; i++) {
    if (arr[i]! < best) {
      best = arr[i]!;
      idx = i;
    }
  }
  return idx;
}

/** Extract a single column (0=x, 1=z) from an Nx2 list of [x, z]. */
function col(pts: CsPoint[], c: 0 | 1): number[] {
  const out = new Array<number>(pts.length);
  for (let i = 0; i < pts.length; i++) out[i] = pts[i]![c];
  return out;
}

function minArr(arr: number[]): number {
  let m = arr[0]!;
  for (let i = 1; i < arr.length; i++) if (arr[i]! < m) m = arr[i]!;
  return m;
}

function maxArr(arr: number[]): number {
  let m = arr[0]!;
  for (let i = 1; i < arr.length; i++) if (arr[i]! > m) m = arr[i]!;
  return m;
}

function norm2(dx: number, dz: number): number {
  return Math.sqrt(dx * dx + dz * dz);
}

// ---------------------------------------------------------------------------
// interpolate_section
// ---------------------------------------------------------------------------

/**
 * Interpolate the airfoil section at an arbitrary spanwise eta from the cached
 * sections. Outside the [min eta, max eta] range falls back to the NEAREST
 * section (endpoint clamping), returning copies so callers can mutate safely.
 */
export function interpolateSection(
  sections: CsSections,
  etaQuery: number,
): { upper: CsPoint[]; lower: CsPoint[]; chord: number; x_le: number } {
  // sorted numeric etas
  const etas = Object.keys(sections)
    .map((k) => parseFloat(k))
    .sort((a, b) => a - b);

  const key = (e: number): string => e.toFixed(4);

  for (let i = 0; i < etas.length - 1; i++) {
    if (etas[i]! <= etaQuery && etaQuery <= etas[i + 1]!) {
      const t = (etaQuery - etas[i]!) / (etas[i + 1]! - etas[i]!);
      const s0 = sections[key(etas[i]!)]!;
      const s1 = sections[key(etas[i + 1]!)]!;
      const u0 = s0.upper;
      const u1 = s1.upper;
      const l0 = s0.lower;
      const l1 = s1.lower;
      const n = Math.min(u0.length, u1.length, 100);

      const t0u = linspace(0, 1, u0.length);
      const t1u = linspace(0, 1, u1.length);
      const t0l = linspace(0, 1, l0.length);
      const t1l = linspace(0, 1, l1.length);
      const tCommon = linspace(0, 1, n);

      const u0x = col(u0, 0);
      const u0z = col(u0, 1);
      const u1x = col(u1, 0);
      const u1z = col(u1, 1);
      const l0x = col(l0, 0);
      const l0z = col(l0, 1);
      const l1x = col(l1, 0);
      const l1z = col(l1, 1);

      const upper = new Array<CsPoint>(n);
      const lower = new Array<CsPoint>(n);
      for (let j = 0; j < n; j++) {
        const tc = tCommon[j]!;
        const ux = interpScalar(tc, t0u, u0x) * (1 - t) + interpScalar(tc, t1u, u1x) * t;
        const uz = interpScalar(tc, t0u, u0z) * (1 - t) + interpScalar(tc, t1u, u1z) * t;
        upper[j] = [ux, uz];
        const lx = interpScalar(tc, t0l, l0x) * (1 - t) + interpScalar(tc, t1l, l1x) * t;
        const lz = interpScalar(tc, t0l, l0z) * (1 - t) + interpScalar(tc, t1l, l1z) * t;
        lower[j] = [lx, lz];
      }

      const chord = s0.chord * (1 - t) + s1.chord * t;
      const xLe = s0.x_le * (1 - t) + s1.x_le * t;
      return { upper, lower, chord, x_le: xLe };
    }
  }

  // Fallback: nearest
  let nearest = etas[0]!;
  let bestD = Math.abs(etas[0]! - etaQuery);
  for (let i = 1; i < etas.length; i++) {
    const d = Math.abs(etas[i]! - etaQuery);
    if (d < bestD) {
      bestD = d;
      nearest = etas[i]!;
    }
  }
  const s = sections[key(nearest)]!;
  // Return copies of the arrays (so callers can mutate safely).
  return {
    upper: s.upper.map((p) => [p[0], p[1]]),
    lower: s.lower.map((p) => [p[0], p[1]]),
    chord: s.chord,
    x_le: s.x_le,
  };
}

// ---------------------------------------------------------------------------
// find_split_point
// ---------------------------------------------------------------------------

interface SplitPoint {
  x: number;
  z: number;
  radius: number;
}

/** Find where dot(P - H, tangent) = 0 on the surface, near the hinge. */
function findSplitPoint(
  surface: CsPoint[],
  hx: number,
  hz: number,
  chord: number,
): SplitPoint | null {
  // mask x-window +/- 0.15*chord around hinge x
  const pts: CsPoint[] = [];
  const lo = hx - 0.15 * chord;
  const hi = hx + 0.15 * chord;
  for (let i = 0; i < surface.length; i++) {
    const x = surface[i]![0];
    if (x >= lo && x <= hi) pts.push(surface[i]!);
  }
  if (pts.length < 5) return null;

  const n = pts.length;
  const f = new Array<number>(n);
  for (let i = 0; i < n; i++) {
    let tx: number;
    let tz: number;
    if (i === 0) {
      tx = pts[1]![0] - pts[0]![0];
      tz = pts[1]![1] - pts[0]![1];
    } else if (i === n - 1) {
      tx = pts[n - 1]![0] - pts[n - 2]![0];
      tz = pts[n - 1]![1] - pts[n - 2]![1];
    } else {
      tx = pts[i + 1]![0] - pts[i - 1]![0];
      tz = pts[i + 1]![1] - pts[i - 1]![1];
    }
    const tn = norm2(tx, tz);
    if (tn > 1e-12) {
      tx /= tn;
      tz /= tn;
    }
    // dot(pts[i] - H, tang)
    f[i] = (pts[i]![0] - hx) * tx + (pts[i]![1] - hz) * tz;
  }

  for (let i = 0; i < n - 1; i++) {
    if (f[i]! * f[i + 1]! < 0) {
      const t = f[i]! / (f[i]! - f[i + 1]!);
      const px = pts[i]![0] + t * (pts[i + 1]![0] - pts[i]![0]);
      const pz = pts[i]![1] + t * (pts[i + 1]![1] - pts[i]![1]);
      return { x: px, z: pz, radius: norm2(px - hx, pz - hz) };
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// compute_cs_shape
// ---------------------------------------------------------------------------

/**
 * Compute all 2D geometry for the control surface at a given section.
 * Pure math - runs in ~ms. Returns null when the section cannot be computed
 * (bad parameters, degenerate section).
 *
 * `gap` and `fillet` are in SECTION UNITS (see the UNITS NOTE above).
 */
export function computeCsShape(
  sections: CsSections,
  eta1: number,
  eta2: number,
  xc: number,
  yh: number,
  gap: number,
  deltaDeg: number,
  sectionEta: number,
  fillet: number,
): CsShapeResult | null {
  const eta = eta1 + sectionEta * (eta2 - eta1);
  const sec = interpolateSection(sections, eta);
  const upper = sec.upper;
  const lower = sec.lower;
  const chord = sec.chord;
  const xLe = sec.x_le;

  // xc/yh: single value shared for both stations (xc1==xc2, yh1==yh2)
  const hingeX = xLe + xc * chord;

  // Find Z at hinge from surfaces
  const tol = 0.02 * chord;
  const nearU: CsPoint[] = [];
  const nearL: CsPoint[] = [];
  for (let i = 0; i < upper.length; i++) {
    if (Math.abs(upper[i]![0] - hingeX) < tol) nearU.push(upper[i]!);
  }
  for (let i = 0; i < lower.length; i++) {
    if (Math.abs(lower[i]![0] - hingeX) < tol) nearL.push(lower[i]!);
  }
  if (nearU.length < 1 || nearL.length < 1) return null;

  const zUpper = maxArr(col(nearU, 1));
  const zLower = minArr(col(nearL, 1));
  const hingeZ = zLower + yh * (zUpper - zLower);

  // Split points
  const su = findSplitPoint(upper, hingeX, hingeZ, chord);
  const sl = findSplitPoint(lower, hingeX, hingeZ, chord);
  if (su === null || sl === null) return null;

  // Spiral arc (cosine blend)
  const aUp = Math.atan2(su.z - hingeZ, su.x - hingeX);
  const aLo = Math.atan2(sl.z - hingeZ, sl.x - hingeX);
  let sweep = aLo - aUp + 2 * Math.PI;
  if (sweep > 2 * Math.PI) sweep -= 2 * Math.PI;

  const nArc = 80;
  const tArr = linspace(0, 1, nArc);
  const angles = new Array<number>(nArc);
  const blend = new Array<number>(nArc);
  const rSpiral = new Array<number>(nArc);
  const rCove = new Array<number>(nArc);
  // In this API gap is already in section-coord units => used directly.
  const gapOff = gap;
  for (let i = 0; i < nArc; i++) {
    angles[i] = aLo - tArr[i]! * sweep;
    blend[i] = (1 - Math.cos(Math.PI * tArr[i]!)) / 2;
    rSpiral[i] = sl.radius + (su.radius - sl.radius) * blend[i]!;
    const slG = sl.radius + gapOff;
    const suG = su.radius + gapOff;
    rCove[i] = slG + (suG - slG) * blend[i]!;
  }

  // Split airfoil: wing = forward of split, CS = aft of split
  const suX = su.x;
  const slX = sl.x;

  const csUpper = filterGE(upper, suX - 0.001); // upper[:,0] >= suX - 0.001
  const csLower = filterGE(lower, slX - 0.001);

  const deltaRad = -(deltaDeg * Math.PI) / 180; // positive delta = TE down = neg math rotation

  // Spiral LE of deflected CS
  const spiralDeflX = new Array<number>(nArc);
  const spiralDeflZ = new Array<number>(nArc);
  for (let i = 0; i < nArc; i++) {
    spiralDeflX[i] = hingeX + rSpiral[i]! * Math.cos(angles[i]! + deltaRad);
    spiralDeflZ[i] = hingeZ + rSpiral[i]! * Math.sin(angles[i]! + deltaRad);
  }

  // Cove arc (on the wing, undeflected)
  const coveXArr = new Array<number>(nArc);
  const coveZArr = new Array<number>(nArc);
  for (let i = 0; i < nArc; i++) {
    coveXArr[i] = hingeX + rCove[i]! * Math.cos(angles[i]!);
    coveZArr[i] = hingeZ + rCove[i]! * Math.sin(angles[i]!);
  }

  // CS contour at 0deg (spiral)
  const spiral0X = new Array<number>(nArc);
  const spiral0Z = new Array<number>(nArc);
  for (let i = 0; i < nArc; i++) {
    spiral0X[i] = hingeX + rSpiral[i]! * Math.cos(angles[i]!);
    spiral0Z[i] = hingeZ + rSpiral[i]! * Math.sin(angles[i]!);
  }

  // trimmed CS surfaces (x > split + 0.002)
  const csUpperTrimmed = csUpper.length > 0 ? filterGT(csUpper, suX + 0.002) : csUpper;
  const csLowerTrimmed = csLower.length > 0 ? filterGT(csLower, slX + 0.002) : csLower;

  // cs_contour_0 = concat(spiral0, csUpperTrimmed, csLowerTrimmed[::-1])
  const csContour0X = concat3(spiral0X, col(csUpperTrimmed, 0), colReversed(csLowerTrimmed, 0));
  const csContour0Z = concat3(spiral0Z, col(csUpperTrimmed, 1), colReversed(csLowerTrimmed, 1));

  // Deflected contour
  const csUpperTrimmedD = rotatePoints(csUpperTrimmed, hingeX, hingeZ, deltaRad);
  const csLowerTrimmedD = rotatePoints(csLowerTrimmed, hingeX, hingeZ, deltaRad);
  const csContourDX = concat3(spiralDeflX, col(csUpperTrimmedD, 0), colReversed(csLowerTrimmedD, 0));
  const csContourDZ = concat3(spiralDeflZ, col(csUpperTrimmedD, 1), colReversed(csLowerTrimmedD, 1));

  // Wing cove: find where cove arc intersects upper and lower surfaces
  const coveUpperHit = findArcSurfaceIntersection(coveXArr, coveZArr, upper);
  const coveLowerHit = findArcSurfaceIntersection(coveXArr, coveZArr, lower);

  let wingUpperTrimmed: CsPoint[];
  let wingLowerTrimmed: CsPoint[];
  let coveTrimmedX: number[];
  let coveTrimmedZ: number[];

  if (coveUpperHit && coveLowerHit) {
    const loI = Math.min(coveLowerHit.i, coveUpperHit.i);
    const hiI = Math.max(coveLowerHit.i, coveUpperHit.i);
    // python: cove_x_arr[lo_i : hi_i + 2]  (end-exclusive)
    coveTrimmedX = coveXArr.slice(loI, hiI + 2);
    coveTrimmedZ = coveZArr.slice(loI, hiI + 2);

    wingUpperTrimmed = filterLE(upper, coveUpperHit.ix + 0.001);
    wingLowerTrimmed = filterLE(lower, coveLowerHit.ix + 0.001);
  } else {
    wingUpperTrimmed = filterLE(upper, suX - 0.002);
    wingLowerTrimmed = filterLE(lower, slX - 0.002);
    coveTrimmedX = coveXArr.slice();
    coveTrimmedZ = coveZArr.slice();
  }

  // Fillet at cove-to-surface junctions.
  // In this API, fillet is already in section-coord units => used directly.
  const filletR = fillet;
  let filletUpperX: number[] = [];
  let filletUpperZ: number[] = [];
  let filletLowerX: number[] = [];
  let filletLowerZ: number[] = [];

  if (filletR > 0.001 && coveUpperHit && coveLowerHit) {
    const fu = computeFillet(
      upper,
      true,
      coveUpperHit.ix,
      coveUpperHit.iz,
      coveXArr,
      coveZArr,
      coveUpperHit.i,
      filletR,
      hingeX,
      hingeZ,
    );
    if (fu.x.length > 0) {
      filletUpperX = fu.x;
      filletUpperZ = fu.z;
    }
    const fl = computeFillet(
      lower,
      false,
      coveLowerHit.ix,
      coveLowerHit.iz,
      coveXArr,
      coveZArr,
      coveLowerHit.i,
      filletR,
      hingeX,
      hingeZ,
    );
    if (fl.x.length > 0) {
      filletLowerX = fl.x;
      filletLowerZ = fl.z;
    }
  }

  // Wing contour assembly
  const partsX: number[][] = [col(wingUpperTrimmed, 0)];
  const partsZ: number[][] = [col(wingUpperTrimmed, 1)];
  if (filletUpperX.length > 0) {
    partsX.push(filletUpperX);
    partsZ.push(filletUpperZ);
  }
  partsX.push(reversed(coveTrimmedX));
  partsZ.push(reversed(coveTrimmedZ));
  if (filletLowerX.length > 0) {
    partsX.push(reversed(filletLowerX));
    partsZ.push(reversed(filletLowerZ));
  }
  partsX.push(colReversed(wingLowerTrimmed, 0));
  partsZ.push(colReversed(wingLowerTrimmed, 1));

  const wingContourX = concatMany(partsX);
  const wingContourZ = concatMany(partsZ);

  return {
    eta,
    chord,
    hinge_x: hingeX,
    hinge_z: hingeZ,
    su,
    sl,
    cs_contour_0: { x: csContour0X, z: csContour0Z },
    cs_contour_defl: { x: csContourDX, z: csContourDZ },
    wing_contour: { x: wingContourX, z: wingContourZ },
    cove_x: coveXArr,
    cove_z: coveZArr,
    spiral_x: spiral0X,
    spiral_z: spiral0Z,
    spiral_defl_x: spiralDeflX,
    spiral_defl_z: spiralDeflZ,
  };
}

/** Zip a {x[], z[]} contour into the [x, z] point list the section tools use. */
export function contourToPoints(c: { x: number[]; z: number[] }): CsPoint[] {
  const n = Math.min(c.x.length, c.z.length);
  const out = new Array<CsPoint>(n);
  for (let i = 0; i < n; i++) out[i] = [c.x[i]!, c.z[i]!];
  return out;
}

// ---------------------------------------------------------------------------
// compute_cs_shape helpers
// ---------------------------------------------------------------------------

/** Rotate an Nx2 point list around (cx, cz) by angleRad. */
function rotatePoints(pts: CsPoint[], cx: number, cz: number, angleRad: number): CsPoint[] {
  const cosA = Math.cos(angleRad);
  const sinA = Math.sin(angleRad);
  const out = new Array<CsPoint>(pts.length);
  for (let i = 0; i < pts.length; i++) {
    const dx = pts[i]![0] - cx;
    const dz = pts[i]![1] - cz;
    out[i] = [cx + dx * cosA - dz * sinA, cz + dx * sinA + dz * cosA];
  }
  return out;
}

/** Filter Nx2 where x >= thr. */
function filterGE(pts: CsPoint[], thr: number): CsPoint[] {
  const out: CsPoint[] = [];
  for (let i = 0; i < pts.length; i++) if (pts[i]![0] >= thr) out.push(pts[i]!);
  return out;
}

/** Filter Nx2 where x > thr. */
function filterGT(pts: CsPoint[], thr: number): CsPoint[] {
  const out: CsPoint[] = [];
  for (let i = 0; i < pts.length; i++) if (pts[i]![0] > thr) out.push(pts[i]!);
  return out;
}

/** Filter Nx2 where x <= thr. */
function filterLE(pts: CsPoint[], thr: number): CsPoint[] {
  const out: CsPoint[] = [];
  for (let i = 0; i < pts.length; i++) if (pts[i]![0] <= thr) out.push(pts[i]!);
  return out;
}

/** Column c of pts reversed (pts[::-1, c]). */
function colReversed(pts: CsPoint[], c: 0 | 1): number[] {
  const out = new Array<number>(pts.length);
  for (let i = 0; i < pts.length; i++) out[i] = pts[pts.length - 1 - i]![c];
  return out;
}

function reversed(arr: number[]): number[] {
  const out = new Array<number>(arr.length);
  for (let i = 0; i < arr.length; i++) out[i] = arr[arr.length - 1 - i]!;
  return out;
}

function concat3(a: number[], b: number[], c: number[]): number[] {
  return a.concat(b, c);
}

function concatMany(parts: number[][]): number[] {
  let out: number[] = [];
  for (let i = 0; i < parts.length; i++) out = out.concat(parts[i]!);
  return out;
}

/** find_arc_surface_intersection - first arc segment crossing the surface. */
function findArcSurfaceIntersection(
  arcX: number[],
  arcZ: number[],
  surfacePts: CsPoint[],
): { i: number; ix: number; iz: number } | null {
  const surfX = col(surfacePts, 0);
  const surfZ = col(surfacePts, 1);
  const sxMin = minArr(surfX);
  const sxMax = maxArr(surfX);
  for (let i = 0; i < arcX.length - 1; i++) {
    const ax = arcX[i]!;
    if (ax < sxMin || ax > sxMax) continue;
    const sz = interpScalar(ax, surfX, surfZ);
    const az = arcZ[i]!;
    const ax2 = arcX[i + 1]!;
    if (ax2 < sxMin || ax2 > sxMax) continue;
    const sz2 = interpScalar(ax2, surfX, surfZ);
    const az2 = arcZ[i + 1]!;
    const d0 = az - sz;
    const d1 = az2 - sz2;
    if (d0 * d1 < 0) {
      const t = d0 / (d0 - d1);
      const ix = ax + t * (ax2 - ax);
      const iz = az + t * (az2 - az);
      return { i, ix, iz };
    }
  }
  return null;
}

/** compute_fillet - tangent arc between the cove and the wing surface. */
function computeFillet(
  surface: CsPoint[],
  surfIsUpper: boolean,
  hitX: number,
  hitZ: number,
  cXArr: number[],
  cZArr: number[],
  arcIdx: number,
  rFillet: number,
  hingeX: number,
  hingeZ: number,
): { x: number[]; z: number[] } {
  const sx = col(surface, 0);
  const szArr = col(surface, 1);

  // hit_i = argmin(|sx - hitX|)
  const absd = new Array<number>(sx.length);
  for (let i = 0; i < sx.length; i++) absd[i] = Math.abs(sx[i]! - hitX);
  let hitI = argmin(absd);
  if (hitI === 0) hitI = 1;
  if (hitI >= sx.length - 1) hitI = sx.length - 2;

  let sTangX = sx[hitI + 1]! - sx[hitI - 1]!;
  let sTangZ = szArr[hitI + 1]! - szArr[hitI - 1]!;
  const sn = norm2(sTangX, sTangZ);
  sTangX /= sn;
  sTangZ /= sn;

  const ci = Math.min(arcIdx, cXArr.length - 2);
  let cTangX = cXArr[ci + 1]! - cXArr[ci]!;
  let cTangZ = cZArr[ci + 1]! - cZArr[ci]!;
  const cn = norm2(cTangX, cTangZ);
  cTangX /= cn;
  cTangZ /= cn;

  // s_norm = [-s_tang[1], s_tang[0]]; negate if upper
  let sNormX = -sTangZ;
  let sNormZ = sTangX;
  if (surfIsUpper) {
    sNormX = -sNormX;
    sNormZ = -sNormZ;
  }

  // c_norm = [-c_tang[1], c_tang[0]]; flip toward hinge
  let cNormX = -cTangZ;
  let cNormZ = cTangX;
  const toHingeX = hingeX - hitX;
  const toHingeZ = hingeZ - hitZ;
  if (cNormX * toHingeX + cNormZ * toHingeZ < 0) {
    cNormX = -cNormX;
    cNormZ = -cNormZ;
  }

  const bisectorX = sNormX + cNormX;
  const bisectorZ = sNormZ + cNormZ;
  const bn = norm2(bisectorX, bisectorZ);
  if (bn < 0.01) return { x: [], z: [] };

  // half_angle = acos(clamp(dot(s_tang, c_tang)))
  let dotTC = sTangX * cTangX + sTangZ * cTangZ;
  if (dotTC > 1) dotTC = 1;
  if (dotTC < -1) dotTC = -1;
  const halfAngle = Math.acos(dotTC);
  if (halfAngle < 0.01) return { x: [], z: [] };
  const d = rFillet / Math.sin(halfAngle / 2);

  const centerX = hitX + (d * bisectorX) / bn;
  const centerZ = hitZ + (d * bisectorZ) / bn;

  const aStart = Math.atan2(-sNormZ, -sNormX);
  const aEnd = Math.atan2(-cNormZ, -cNormX);

  let da = aEnd - aStart;
  if (da > Math.PI) da -= 2 * Math.PI;
  if (da < -Math.PI) da += 2 * Math.PI;

  const nFil = 15;
  const tF = linspace(0, 1, nFil);
  const fx = new Array<number>(nFil);
  const fz = new Array<number>(nFil);
  for (let i = 0; i < nFil; i++) {
    const aF = aStart + tF[i]! * da;
    fx[i] = centerX + rFillet * Math.cos(aF);
    fz[i] = centerZ + rFillet * Math.sin(aF);
  }
  return { x: fx, z: fz };
}
